import { useState, useEffect } from "react";
import PiRefreshStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiRefreshStroke";

import { useClass, useSyncClass, useRefetchAfterSync } from "@/api/hooks";
import { buttonVariants } from "@markeroo/theme";
import { ui, notifications } from "@/strings";
import { useNotification } from "@/providers/NotificationProvider";
import { useAnalytics } from "@/providers/AnalyticsProvider";
import { formatRelativeTime } from "@/utils/formatRelativeTime";
import { useGCSyncStore } from "@/stores/gcSync";
import { useGCTokenExpiration } from "../hooks";

interface SyncButtonProps {
  classId: string;
}

export function SyncButton({ classId }: SyncButtonProps) {
  const syncMutation = useSyncClass();
  const refetchAfterSync = useRefetchAfterSync();
  const { data: classData } = useClass(classId);
  const { show } = useNotification();
  const { track } = useAnalytics();
  const { isGCTokenExpired, handleGCTokenExpired } = useGCTokenExpiration();
  const { startSyncing, stopSyncing, isSyncing: isStoreSyncing } = useGCSyncStore();

  // Force re-render every minute to keep "Synced x ago" text live
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!classData?.gcCourseId) {
    return null; // Not linked to GC
  }

  const handleSync = () => {
    // Atomic check-and-set prevents duplicate syncs
    const syncStarted = startSyncing(classId);
    if (!syncStarted) {
      // Already syncing (either from auto-sync or another manual trigger)
      return;
    }
    // mutateAsync so stopSyncing still runs if this button unmounts mid-sync
    void syncMutation.mutateAsync(classId).then(
      (result) => {
        stopSyncing(classId);
        void refetchAfterSync([classId]);
        if (result.submissions > 0) {
          show(notifications.googleClassroom.syncComplete(result.submissions), "success");
        }
        track("gc_manual_sync_completed", {
          classId,
          submissions: result.submissions,
          students: result.students,
          assignments: result.assignments,
        });
      },
      (error: unknown) => {
        stopSyncing(classId);
        // Handle token expiration - shows banner immediately
        if (isGCTokenExpired(error)) {
          handleGCTokenExpired("manual_sync");
          track("gc_manual_sync_failed", { classId, error_type: "token_expired" });
          return;
        }
        // Handle other specific error types
        if (error instanceof Error && "response" in error) {
          const axiosError = error as { response?: { status?: number } };
          const status = axiosError.response?.status;
          if (status === 429) {
            // Rate limited
            show(notifications.googleClassroom.rateLimited, "error");
            track("gc_manual_sync_failed", { classId, error_type: "rate_limited" });
            return;
          }
          if (status === 404) {
            // Not linked
            show(notifications.googleClassroom.notLinked, "error");
            track("gc_manual_sync_failed", { classId, error_type: "not_linked" });
            return;
          }
        }
        // Generic error fallback
        show(notifications.googleClassroom.syncFailed, "error");
        track("gc_manual_sync_failed", { classId, error_type: "unknown" });
      }
    );
  };

  const lastSyncedText = classData.gcLastSyncedAt
    ? ui.googleClassroom.syncedAgo(formatRelativeTime(classData.gcLastSyncedAt))
    : ui.googleClassroom.syncedAgo(ui.googleClassroom.never);

  const isSyncing = syncMutation.isPending || isStoreSyncing(classId);

  return (
    <button
      onClick={handleSync}
      disabled={isSyncing}
      className={`${buttonVariants.secondary} !items-center !justify-start !py-1 min-w-[240px]`}
    >
      <PiRefreshStroke size={16} className={`mr-2 ${isSyncing ? "animate-spin" : ""}`} />
      <span className="flex flex-col items-start">
        <span className="leading-tight">{isSyncing ? ui.googleClassroom.syncing : ui.googleClassroom.syncFromGoogleClassroom}</span>
        <span className="text-label-xs text-text-theme-muted font-normal leading-tight">{lastSyncedText}</span>
      </span>
    </button>
  );
}
